import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  Button,
  TextField,
  CircularProgress,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio
} from "@mui/material";

import MyLocationIcon from "@mui/icons-material/MyLocation";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import CloseIcon from "@mui/icons-material/Close";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import OlSelect from "ol/interaction/Select";
import { click } from "ol/events/condition";
import { extend, createEmpty, isEmpty } from "ol/extent";

import ToolDialog from "../ToolDialog";

const OPTIMIZED_ROUTE_URL =
  "https://app.mapgeoid.com/api-service/routing/google_optimized_route";

// style
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import CircleStyle from 'ol/style/Circle';
import Text from 'ol/style/Text';

// 🔵 Depot style (green pin)
const depotStyle = new Style({
  image: new Icon({
    src: 'http://maps.google.com/mapfiles/kml/pushpin/grn-pushpin.png',
    scale: 1,
    anchor: [0.5, 1],
  }),
});

// 🟠 Route style (dotted)
const routeStyle = new Style({
  stroke: new Stroke({
    color: 'rgba(255, 85, 0, 1)',
    width: 3,
    lineDash: [10, 10],
  }),
});

// 🔢 Inter style (circle with index)
const createInterStyle = (index) =>
  new Style({
    image: new CircleStyle({
      radius: 12,
      fill: new Fill({ color: 'rgba(255,165,0,0.85)' }),
      stroke: new Stroke({ color: '#fff', width: 2 }),
    }),
    text: new Text({
      text: String(index + 1),
      font: 'bold 12px sans-serif',
      fill: new Fill({ color: '#000' }),
    }),
  });
// ─── geometry filter helpers ──────────────────────────────────────────────────

function getPointLayers(layers) {
  return Object.entries(layers).filter(
    ([, info]) => info.vectorLayer?.get("geometry_type") === "Point"
  );
}

function getLineLayers(layers) {
  return Object.entries(layers).filter(
    ([, info]) => info.vectorLayer?.get("geometry_type") === "LineString"
  );
}

// ─── map helpers ──────────────────────────────────────────────────────────────

function zoomToFeature(map, feature) {
  const geom = feature.getGeometry();
  if (!geom) return;
  map.getView().fit(geom.getExtent(), {
    maxZoom: 18,
    duration: 400,
    padding: [60, 60, 60, 60],
  });
}

function zoomToFeatures(map, features) {
  if (!features.length) return;
  const extent = createEmpty();
  features.forEach((f) => {
    const g = f.getGeometry();
    if (g) extend(extent, g.getExtent());
  });
  if (!isEmpty(extent))
    map.getView().fit(extent, {
      maxZoom: 18,
      duration: 400,
      padding: [80, 80, 80, 80],
    });
}

// ─── FeatureChip ──────────────────────────────────────────────────────────────

function FeatureChip({ index, feature, chipType, onRemove, onLocate, onZoom }) {
  const id = feature.getId() ?? "—";
  const name = feature.get("name") ?? feature.get("Name") ?? feature.get("NAME") ?? feature.get("label") ?? null;
  const label = name ? `[${index}]: ${id} (${name})` : String(id);

  const borderColor =
    chipType === "depot"
      ? "warning.light"
      : chipType === "route"
        ? "secondary.light"
        : "divider";

  const bg =
    chipType === "depot"
      ? "rgba(255,193,7,0.08)"
      : chipType === "route"
        ? "rgba(156,39,176,0.06)"
        : "background.paper";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        border: "0.5px solid",
        borderColor,
        borderRadius: "99px",
        background: bg,
        pl: 1.25,
        pr: 0.25,
        py: 0.25,
        gap: 0,
      }}
    >
      <Typography variant="caption" sx={{ fontSize: 12, lineHeight: 1, mr: 0.5 }}>
        {label}
      </Typography>

      <Tooltip title="Select & zoom to feature">
        <IconButton size="small" sx={{ width: 20, height: 20, p: 0 }} onClick={() => onLocate(feature)}>
          <MyLocationIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Zoom to feature">
        <IconButton size="small" sx={{ width: 20, height: 20, p: 0 }} onClick={() => onZoom(feature)}>
          <ZoomInMapIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Remove">
        <IconButton size="small" sx={{ width: 20, height: 20, p: 0 }} onClick={() => onRemove(feature)}>
          <CloseIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}




// ─── SelectionBox ─────────────────────────────────────────────────────────────

function SelectionBox({
  features,
  chipType,
  isActive,
  emptyLabel,
  onRemove,
  onLocate,
  onZoom,
  onZoomAll,
  onReorder,
  onClick,
}) {
  const dragIndex = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  function handleDragStart(e, i) {
    dragIndex.current = i;
    e.dataTransfer.effectAllowed = "move";
    // hide the default ghost — we style the chip itself instead
    e.dataTransfer.setData("text/plain", String(i));
  }

  function handleDragOver(e, i) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIdx(i);
  }

  function handleDrop(e, i) {
    e.preventDefault();
    const from = dragIndex.current;
    if (from == null || from === i) return;
    const updated = [...features];
    const [moved] = updated.splice(from, 1);
    updated.splice(i, 0, moved);
    onReorder?.(updated);
  }

  function handleDragEnd() {
    dragIndex.current = null;
    setDragOverIdx(null);
  }

  return (
    <Box onClick={onClick}>
      <Box
        sx={{
          minHeight: 38,
          border: "0.5px solid",
          borderColor: isActive ? "primary.main" : "divider",
          borderRadius: 1,
          background: isActive ? "rgba(25,118,210,0.04)" : "action.hover",
          p: 0.75,
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          alignItems: features.length ? "flex-start" : "center",
        }}
      >
        {features.length === 0 ? (
          <Typography
            variant="caption"
            color={isActive ? "primary.main" : "text.disabled"}
            fontStyle="italic"
          >
            {isActive ? "Click a feature on the map…" : emptyLabel}
          </Typography>
        ) : (
          features.map((f, i) => (
            <Box
              key={f.getId() ?? i}
              draggable
              onDragStart={(e) => handleDragStart(e, i)}
              onDragOver={(e) => handleDragOver(e, i)}
              onDrop={(e) => handleDrop(e, i)}
              onDragEnd={handleDragEnd}
              sx={{
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
                opacity: dragIndex.current === i ? 0.3 : 1,
                outline: dragOverIdx === i && dragIndex.current !== i
                  ? "1.5px dashed"
                  : "none",
                outlineColor: "primary.main",
                borderRadius: "99px",
                transition: "opacity 0.15s, outline 0.1s",
              }}
            >
              <FeatureChip
                index={i + 1}
                feature={f}
                chipType={chipType}
                onRemove={onRemove}
                onLocate={onLocate}
                onZoom={onZoom}
              />
            </Box>
          ))
        )}
      </Box>

      {features.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 0.5,
          }}
        >
          <Typography variant="caption" color="text.disabled">
            {features.length} selected
          </Typography>
          <Tooltip title="Zoom to fit all selected">
            <Button
              size="small"
              variant="text"
              startIcon={<CenterFocusStrongIcon sx={{ fontSize: 13 }} />}
              onClick={onZoomAll}
              sx={{
                fontSize: 11,
                py: 0,
                minWidth: 0,
                textTransform: "none",
                color: "text.secondary",
              }}
            >
              Zoom to all
            </Button>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, badge, badgeColor = "default", children }) {
  return (
    <Box sx={{ px: 1.5, py: 1.25 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: 10,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "text.secondary",
          }}
        >
          {title}
        </Typography>
        {badge && (
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              px: 0.75,
              py: 0.1,
              borderRadius: "99px",
              fontWeight: 500,
              background:
                badgeColor === "required"
                  ? "rgba(211,47,47,0.1)"
                  : "rgba(46,125,50,0.1)",
              color:
                badgeColor === "required" ? "error.dark" : "success.dark",
            }}
          >
            {badge}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function OptimizedRoutingTool({ title, map, layers, onClose, onNewLayerCallback, project }) {

  // ── logs ──
  const [logs, setLogs] = useState([]);   // [{ type: "info"|"error"|"success", message: string }]
  const logsEndRef = useRef(null);

  const pushLog = (type, message) => {
    setLogs((prev) => [...prev, { type, message }]);
  };


  // auto-scroll log box to bottom on new entry
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  // ── depot ──
  const [depotLayerId, setDepotLayerId] = useState("");
  const [depotMode, setDepotMode] = useState("first");   // "first" | "map"
  const [depotFeatures, setDepotFeatures] = useState([]);        // max 1

  // ── intermediate ──
  const [interLayerId, setInterLayerId] = useState("");
  const [interMode, setInterMode] = useState("all");     // "all" | "map"
  const [interFeatures, setInterFeatures] = useState([]);

  // ── existing route ──
  const [routeLayerId, setRouteLayerId] = useState("");
  const [routeMode, setRouteMode] = useState("all");     // "all" | "map"
  const [routeFeatures, setRouteFeatures] = useState([]);

  // ── routing options ──
  const [optimized, setOptimized] = useState(true);

  // ── output ──
  const [outputMode, setOutputMode] = useState("new"); // "append" | "new" | "overwrite"
  const [outputName, setOutputName] = useState("Optimized Route");
  const [outputLayerId, setOutputLayerId] = useState("");

  // ── loading ──
  const [loading, setLoading] = useState(false);

  // ── active OL selection target: "depot" | "inter" | "route" | null ──
  const [selectTarget, setSelectTarget] = useState(null);
  const olSelectRef = useRef(null);

  // derived VectorLayer refs
  const depotVL = layers[depotLayerId]?.vectorLayer ?? null;
  const interVL = layers[interLayerId]?.vectorLayer ?? null;
  const routeVL = layers[routeLayerId]?.vectorLayer ?? null;

  const activeVL =
    selectTarget === "depot" ? depotVL :
      selectTarget === "inter" ? interVL :
        selectTarget === "route" ? routeVL : null;


  // STYLE UPDATE effect - apply styles only to selected features
  useEffect(() => {
    const applyStyle = (vl, selectedFeatures, styleOrFn, excludedFeatures = []) => {
      if (!vl) return;

      const source = vl.getSource();
      if (!source) return;

      // Reset styles for all features EXCEPT those from other selections
      source.forEachFeature((feature) => {
        const fid = feature.getId();
        // Don't reset if this feature is in another selection
        if (!excludedFeatures.some(f => f.getId() === fid)) {
          feature.setStyle(null); // reset to layer's default style
        }
      });

      // Apply custom style only to selected features
      selectedFeatures.forEach((selectedFeature, idx) => {
        const feature = source.getFeatureById(selectedFeature.getId());
        if (feature) {
          // If styleOrFn is a function, call it with index; otherwise use directly
          const style = typeof styleOrFn === 'function' ? styleOrFn(idx) : styleOrFn;
          feature.setStyle(style);
        }
      });
    };

    // When applying styles, exclude features from other selections on the same layer
    // Route is independent - only depot and inter can conflict on the same layer
    applyStyle(depotVL, depotFeatures, depotStyle, interFeatures);
    applyStyle(interVL, interFeatures, createInterStyle, depotFeatures);
    applyStyle(routeVL, routeFeatures, routeStyle);

  }, [depotFeatures, interFeatures, routeFeatures, depotVL, interVL, routeVL]);

  useEffect(() => {
    depotVL?.changed();
    interVL?.changed();
    routeVL?.changed();
  }, [depotFeatures, interFeatures, routeFeatures]);

  // ── mount / unmount OL Select interaction ────────────────────────────────────
  useEffect(() => {
    if (olSelectRef.current) {
      map.removeInteraction(olSelectRef.current);
      olSelectRef.current = null;
    }
    if (!selectTarget || !activeVL) return;

    const interaction = new OlSelect({
      layers: [activeVL],
      // style: null, // keep your layer's own style
    });

    interaction.on("select", (evt) => {
      const [feature] = evt.selected;
      if (!feature) return;
      interaction.getFeatures().clear();

      const featureId = feature.getId();

      if (selectTarget === "depot") {
        // Check if this feature is already selected as inter
        if (depotLayerId === interLayerId && interFeatures.some((f) => f.getId() === featureId)) {
          return; // Prevent selecting same feature for both depot and inter
        }
        setDepotFeatures([feature]);
      } else if (selectTarget === "inter") {
        // Check if this feature is already selected as depot
        if (interLayerId === depotLayerId && depotFeatures.some((f) => f.getId() === featureId)) {
          return; // Prevent selecting same feature for both depot and inter
        }
        setInterFeatures((prev) =>
          prev.some((f) => f.getId() === featureId) ? prev : [...prev, feature]
        );
      } else if (selectTarget === "route") {
        setRouteFeatures((prev) =>
          prev.some((f) => f.getId() === featureId) ? prev : [...prev, feature]
        );
      }
    });

    map.addInteraction(interaction);
    olSelectRef.current = interaction;
    return () => {
      map.removeInteraction(interaction);
      olSelectRef.current = null;
    };
  }, [selectTarget, activeVL, map]);

  // ── reset on layer change ─────────────────────────────────────────────────
  useEffect(() => {
    setDepotMode("first");
    setDepotFeatures([]);
    setSelectTarget((t) => (t === "depot" ? null : t));
  }, [depotLayerId]);

  useEffect(() => {
    setInterMode("all");
    setInterFeatures([]);
    setSelectTarget((t) => (t === "inter" ? null : t));
  }, [interLayerId]);

  useEffect(() => {
    setRouteMode("all");
    setRouteFeatures([]);
    setSelectTarget((t) => (t === "route" ? null : t));
  }, [routeLayerId]);

  // ── mode change handlers ──────────────────────────────────────────────────
  const handleDepotModeChange = (val) => {
    setDepotMode(val);
    setDepotFeatures([]);
    setSelectTarget(val === "map" ? "depot" : null);
  };

  const handleInterModeChange = (val) => {
    setInterMode(val);
    setInterFeatures([]);
    setSelectTarget(val === "map" ? "inter" : null);
  };

  const handleRouteModeChange = (val) => {
    setRouteMode(val);
    setRouteFeatures([]);
    setSelectTarget(val === "map" ? "route" : null);
  };

  // ── chip actions ──────────────────────────────────────────────────────────
  const handleLocate = (feature) => {
    zoomToFeature(map, feature);
    if (olSelectRef.current) {
      olSelectRef.current.getFeatures().clear();
      olSelectRef.current.getFeatures().push(feature);
      setTimeout(() => olSelectRef.current?.getFeatures().clear(), 1500);
    }
  };

  const handleZoom = (feature) => zoomToFeature(map, feature);

  // ── run ───────────────────────────────────────────────────────────────────
  const canRun = !!interLayerId;

  const handleRun = async () => {
    if (!canRun) return;
    setLogs([]);
    setLoading(true);

    const depotFeatureId =
      depotMode === "map" && depotFeatures.length
        ? depotFeatures[0].getId()
        : "first";

    const intermediateIds =
      interMode === "map" && interFeatures.length
        ? interFeatures.map((f) => f.getId())
        : "all";

    const existingRouteIds =
      routeLayerId && routeMode === "map" && routeFeatures.length
        ? routeFeatures.map((f) => f.getId())
        : routeLayerId
          ? "all"
          : undefined;

    // Capitalize write_mode: "new" → "New", "append" → "Append", "overwrite" → "Overwrite"
    const writeMode = outputMode.charAt(0).toUpperCase() + outputMode.slice(1);

    const payload = {
      project_id: project.id,
      ...(depotLayerId && {
        depot_layer_id: depotLayerId,
        depot_selected_feature_id: depotFeatureId,
      }),
      waypoint_layer: interLayerId,
      waypoint_layer_selected_feature_ids: intermediateIds,
      ...(routeLayerId && {
        existing_route_layer_id: routeLayerId,
        existing_route_layer_selected_feature_ids: existingRouteIds,
      }),
      waypoint_position_optimize: optimized,
      ...(outputLayerId && { output_layer_id: outputLayerId }),
      ...(outputName && { output_layer_name: outputName }),
      write_mode: writeMode,
    };

    try {
      const res = await fetch(OPTIMIZED_ROUTE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      // ── stream NDJSON lines ───────────────────────────────────────────────
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop(); // keep incomplete last line in buffer

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          try {
            const msg = JSON.parse(trimmed);

            if (msg.end) {
              // Handle layer metadata response from backend
              pushLog("success", "Route computed successfully.");

              // check whether the layer exists or is new
              if (layers[msg.layer_meta.id]) {
                // existing layer
                layers[msg.layer_meta.id].vectorLayer.getSource().refresh();
                pushLog("success", `Layer "${msg.layer_meta.name}" updated successfully.`);
              } else if (onNewLayerCallback) {
                onNewLayerCallback(msg.layer_meta);
                pushLog("success", `Layer "${msg.layer_meta.name}" added successfully.`);
              }
            } else {
              // "info" | "error" | any other type — push to log as-is
              pushLog(msg.type ?? "info", msg.message ?? trimmed);
            }
          } catch (err) {
            // plain text line — show as info
            pushLog("error", `Unexpected Exception: ${err.message}`);
          }
        }
      }
    } catch (err) {
      pushLog("error", `Request failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ── derived layer lists ───────────────────────────────────────────────────
  const pointLayerEntries = getPointLayers(layers);
  const lineLayerEntries = getLineLayers(layers);

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <ToolDialog title={title} open onClose={onClose} sx={{ width: 640 }}>

      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)" }}>

        {/* ── LEFT ── */}
        <Box sx={{ borderRight: "0.5px solid", borderColor: "divider", display: "flex", flexDirection: "column" }}>


          <Box sx={{ maxHeight: "50vh", overflowY: "auto" }}>
            {/* ── DEPOT ── */}
            <Section title="Depot" badge="Optional" badgeColor="optional">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Layer (point only)</Typography>
                  <Select
                    fullWidth size="small" displayEmpty
                    value={depotLayerId}
                    onChange={(e) => setDepotLayerId(e.target.value)}
                    MenuProps={{ style: { zIndex: 2000 } }}
                    sx={{ mt: 0.5 }}
                    renderValue={(v) =>
                      v ? (layers[v]?.name ?? v) : <em style={{ color: "rgba(0,0,0,0.38)" }}>— select depot layer —</em>
                    }
                  >
                    <MenuItem value=""><em style={{ color: "rgba(0,0,0,0.38)" }}>None</em></MenuItem>
                    {pointLayerEntries.map(([id, info]) => (
                      <MenuItem key={id} value={id}>{info.name}</MenuItem>
                    ))}
                  </Select>
                </Box>

                {depotLayerId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Feature selection</Typography>
                    <Select
                      fullWidth size="small"
                      value={depotMode}
                      onChange={(e) => handleDepotModeChange(e.target.value)}
                      MenuProps={{ style: { zIndex: 2000 } }}
                      sx={{ mt: 0.5 }}
                    >
                      <MenuItem value="first">First feature (default)</MenuItem>
                      <MenuItem value="map">Select from map</MenuItem>
                    </Select>
                  </Box>
                )}

                {depotLayerId && depotMode === "map" && (
                  <SelectionBox
                    features={depotFeatures}
                    chipType="depot"
                    isActive={selectTarget === "depot"}
                    emptyLabel="First feature (default)"
                    onRemove={(f) => setDepotFeatures((p) => p.filter((x) => x !== f))}
                    onLocate={handleLocate}
                    onZoom={handleZoom}
                    onZoomAll={() => zoomToFeatures(map, depotFeatures)}
                    onReorder={(reordered) => setDepotFeatures(reordered)}
                    onClick={() => setSelectTarget(selectTarget === "depot" ? null : "depot")}
                  />
                )}
              </Box>
            </Section>

            <Divider />

            {/* ── INTERMEDIATE STOPS ── */}
            <Section title="Intermediate Stops" badge="Required" badgeColor="required">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Layer</Typography>
                  <Select
                    fullWidth size="small" displayEmpty
                    value={interLayerId}
                    onChange={(e) => setInterLayerId(e.target.value)}
                    MenuProps={{ style: { zIndex: 2000 } }}
                    sx={{ mt: 0.5 }}
                    renderValue={(v) =>
                      v ? (layers[v]?.name ?? v) : <em style={{ color: "rgba(0,0,0,0.38)" }}>— select layer —</em>
                    }
                  >
                    <MenuItem value=""><em style={{ color: "rgba(0,0,0,0.38)" }}>None</em></MenuItem>
                    {pointLayerEntries.map(([id, info]) => (
                      <MenuItem key={id} value={id}>{info.name}</MenuItem>
                    ))}
                  </Select>
                </Box>

                {interLayerId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Feature selection</Typography>
                    <Select
                      fullWidth size="small"
                      value={interMode}
                      onChange={(e) => handleInterModeChange(e.target.value)}
                      MenuProps={{ style: { zIndex: 2000 } }}
                      sx={{ mt: 0.5 }}
                    >
                      <MenuItem value="all">All features (default)</MenuItem>
                      <MenuItem value="map">Select from map</MenuItem>
                    </Select>
                  </Box>
                )}

                {interLayerId && interMode === "map" && (
                  <SelectionBox
                    features={interFeatures}
                    chipType="inter"
                    isActive={selectTarget === "inter"}
                    emptyLabel="All features (default)"
                    onRemove={(f) => setInterFeatures((p) => p.filter((x) => x !== f))}
                    onLocate={handleLocate}
                    onZoom={handleZoom}
                    onZoomAll={() => zoomToFeatures(map, interFeatures)}
                    onReorder={(reordered) => setInterFeatures(reordered)}
                    onClick={() => setSelectTarget(selectTarget === "inter" ? null : "inter")}
                  />
                )}
              </Box>
            </Section>

            <Divider />

            {/* ── EXISTING ROUTE ── */}
            <Section title="Existing Route" badge="Optional" badgeColor="optional">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Layer (linestring — existing broadband)
                  </Typography>
                  <Select
                    fullWidth size="small" displayEmpty
                    value={routeLayerId}
                    onChange={(e) => setRouteLayerId(e.target.value)}
                    MenuProps={{ style: { zIndex: 2000 } }}
                    sx={{ mt: 0.5 }}
                    renderValue={(v) =>
                      v ? (layers[v]?.name ?? v)
                        : <em style={{ color: "rgba(0,0,0,0.38)" }}>
                          {outputMode === "append" ? "— select layer to append —" : "— select layer to overwrite —"}
                        </em>
                    }
                  >
                    <MenuItem value=""><em style={{ color: "rgba(0,0,0,0.38)" }}>None</em></MenuItem>
                    {lineLayerEntries.map(([id, info]) => (
                      <MenuItem key={id} value={id}>{info.name}</MenuItem>
                    ))}
                  </Select>
                </Box>

                {routeLayerId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Feature selection</Typography>
                    <Select
                      fullWidth size="small"
                      value={routeMode}
                      onChange={(e) => handleRouteModeChange(e.target.value)}
                      MenuProps={{ style: { zIndex: 2000 } }}
                      sx={{ mt: 0.5 }}
                    >
                      <MenuItem value="all">All features (default)</MenuItem>
                      <MenuItem value="map">Select from map</MenuItem>
                    </Select>
                  </Box>
                )}

                {routeLayerId && routeMode === "map" && (
                  <SelectionBox
                    features={routeFeatures}
                    chipType="route"
                    isActive={selectTarget === "route"}
                    emptyLabel="All features (default)"
                    onRemove={(f) => setRouteFeatures((p) => p.filter((x) => x !== f))}
                    onLocate={handleLocate}
                    onZoom={handleZoom}
                    onZoomAll={() => zoomToFeatures(map, routeFeatures)}
                    onReorder={(reordered) => setRouteFeatures(reordered)}
                    onClick={() => setSelectTarget(selectTarget === "route" ? null : "route")}
                  />
                )}
              </Box>
            </Section>

            <Divider />

            {/* ── ROUTING OPTIONS ── */}
            <Section title="Routing Options" badge="Required" badgeColor="required">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={optimized}
                    onChange={(e) => setOptimized(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Auto-optimize stop order</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {optimized
                        ? "Algorithm finds shortest path automatically"
                        : "Stops visited in the order selected"}
                    </Typography>
                  </Box>
                }
                sx={{ ml: 0, alignItems: "flex-start", gap: 1 }}
              />
            </Section>

            <Divider />

            {/* ── OUTPUT LAYER ── */}
            <Section title="Output Layer" badge="Required" badgeColor="required">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>

                {/* Radio group */}
                <RadioGroup
                  row
                  value={outputMode}
                  onChange={(e) => { setOutputMode(e.target.value); setOutputName(""); setOutputLayerId(""); }}
                >
                  <FormControlLabel value="append" control={<Radio size="small" />} label={<Typography variant="caption">Append</Typography>} />
                  <FormControlLabel value="new" control={<Radio size="small" />} label={<Typography variant="caption">New</Typography>} />
                  <FormControlLabel value="overwrite" control={<Radio size="small" />} label={<Typography variant="caption">Overwrite</Typography>} />
                </RadioGroup>

                {/* Conditional field */}
                {outputMode === "new" ? (
                  <TextField
                    fullWidth size="small"
                    placeholder="Enter new layer name"
                    value={outputName}
                    onChange={(e) => setOutputName(e.target.value)}
                  />
                ) : (
                  <Select
                    fullWidth size="small" displayEmpty
                    value={outputLayerId}
                    onChange={(e) => setOutputLayerId(e.target.value)}
                    MenuProps={{ style: { zIndex: 2000 } }}
                    renderValue={(v) =>
                      v ? (layers[v]?.name ?? v)
                        : <em style={{ color: "rgba(0,0,0,0.38)" }}>
                          {outputMode === "append" ? "— select layer to append —" : "— select layer to overwrite —"}
                        </em>
                    }
                  >
                    {Object.entries(layers).map(([id, info]) => (
                      <MenuItem key={id} value={id}>{info.name}</MenuItem>
                    ))}
                  </Select>
                )}

              </Box>
            </Section>

          </Box>
          <Divider />



          {/* ── RUN ── */}
          <Box sx={{ px: 1.5, py: 1.25 }}>
            {!interLayerId && (
              <Typography variant="caption" color="text.disabled" display="block" mb={0.75}>
                Select an intermediate stops layer to run
              </Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              size="small"
              disabled={!canRun || loading}
              onClick={handleRun}
              startIcon={
                loading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <PlayArrowIcon fontSize="small" />
                )
              }
            >
              {loading ? "Computing route…" : "Run Optimized Routing"}
            </Button>
          </Box>
        </Box>
        {/* ── RIGHT: LOG ── */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            px: 1.5, py: 1, borderBottom: "0.5px solid", borderColor: "divider",
          }}>
            <Typography variant="caption" sx={{
              fontSize: 10, fontWeight: 600, textTransform: "uppercase",
              letterSpacing: "0.06em", color: "text.secondary",
            }}>
              Log
            </Typography>
            <Button size="small" variant="text"
              onClick={() => setLogs([])}
              sx={{ fontSize: 11, py: 0, minWidth: 0, textTransform: "none", color: "text.disabled" }}
            >
              Clear
            </Button>
          </Box>

          <Box sx={{
            flex: 1, px: 1.5, py: 1, minHeight: 120,
            maxHeight: "60vh", overflowY: "auto",
            display: "flex", flexDirection: "column", gap: 0.3,
          }}>
            {logs.length === 0 ? (
              <Typography variant="caption" sx={{ fontSize: 11, color: "text.disabled", fontStyle: "italic" }}>
                Run to see logs…
              </Typography>
            ) : logs.map((log, i) => (
              <Box key={i} sx={{ display: "flex", gap: 0.6, alignItems: "flex-start" }}>
                <Typography variant="caption" sx={{
                  fontSize: 11, flexShrink: 0, mt: "1px",
                  color: log.type === "error" ? "error.main" :
                    log.type === "success" ? "success.main" :
                      log.type === "warn" ? "warning.dark" : "text.disabled",
                }}>
                  {log.type === "error" ? "✕" : log.type === "success" ? "✓" : log.type === "warn" ? "⚠" : "›"}
                </Typography>
                <Typography variant="caption" sx={{
                  fontSize: 11, fontFamily: "var(--font-mono)", lineHeight: 1.5,
                  color: log.type === "error" ? "error.main" :
                    log.type === "success" ? "success.main" :
                      log.type === "warn" ? "warning.dark" : "text.secondary",
                }}>
                  {log.message}
                </Typography>
              </Box>
            ))}
            <div ref={logsEndRef} />
          </Box>
        </Box>
      </Box>
    </ToolDialog>
  );
}